class Roles {
  static SUPER_ADMIN = 10;
  static ADMIN = 15;
  static SUBOPS = 20;
  static ADOPS = 30;
  static YIELD_MANAGER = 37;
  static SALES_REP = 45;
  static PUBLISHER = 60;
  static SUBSCRIPTION_PUBLISHER = 65;

  /**
   * All assignable roles as { value, label } objects (for selects/dropdowns).
   */
  static all() {
    return [
      { value: Roles.SUPER_ADMIN,               label: 'Super Admin' },
      { value: Roles.ADMIN,                     label: 'Admin' },
      { value: Roles.SUBOPS,                    label: 'SubOps' },
      { value: Roles.ADOPS,                     label: 'AdOps' },
      { value: Roles.YIELD_MANAGER,             label: 'Yield Manager' },
      { value: Roles.SALES_REP,                 label: 'Sales Representative' },
      { value: Roles.PUBLISHER,                 label: 'Publisher' },
      { value: Roles.SUBSCRIPTION_PUBLISHER,    label: 'Subscription Publisher' },
    ];
  }

  /**
   * All role label strings (for filter dropdowns).
   */
  static labels() {
    return Roles.all().map(r => r.label);
  }

  /**
   * Get the display label for a role value.
   */
  static getLabel(value) {
    return Roles.all().find(r => r.value === value)?.label ?? '';
  }

  /**
   * Roles that a user with `minRole` is allowed to assign
   * (cannot assign roles above their own).
   */
  static visibleTo(minRole) {
    if (!minRole) return Roles.all();
    return Roles.all().filter(r => r.value >= minRole);
  }

  // --- Role check helpers ---

  /** Super Admin only (role <= 10) */
  static isSuperAdmin(role) {
    return !!role && role <= Roles.SUPER_ADMIN;
  }

  /** Admin or above (role <= 15) */
  static isAdmin(role) {
    return !!role && role <= Roles.ADMIN;
  }

  /** SubOps exactly */
  static isSubOps(role) {
    return role === Roles.SUBOPS;
  }

  /** AdOps or admin-tier (role <= 15 || role === 30) */
  static isAdOps(role) {
    return !!role && (role <= Roles.ADMIN || role === Roles.ADOPS);
  }

  /** Yield Manager or admin-tier */
  static isYieldManager(role) {
    return !!role && (role <= Roles.ADMIN || role === Roles.YIELD_MANAGER);
  }

  /** Sales Representative or admin-tier */
  static isSalesperson(role) {
    return !!role && (role <= Roles.ADMIN || role === Roles.SALES_REP);
  }

  /** Any operational employee (admin-tier, AdOps, YieldManager, SalesRep, SubOps) */
  static isEmployee(role) {
    return !!role && (role <= Roles.ADMIN || [Roles.ADOPS, Roles.YIELD_MANAGER, Roles.SALES_REP, Roles.SUBOPS].includes(role));
  }

  /** Any internal role up to and including AdOps (covers SubOps too: role <= 30) */
  static isInternal(role) {
    return !!role && role <= Roles.ADOPS;
  }

  /** Publisher (role === 60) */
  static isOnlyPublisher(role) {
    return role === Roles.PUBLISHER;
  }

  /** Subscription Publisher (role === 65) */
  static isSubscriptionPublisher(role) {
    return role === Roles.SUBSCRIPTION_PUBLISHER;
  }

  /** Publisher or Subscription Publisher */
  static isPublisher(role) {
    return Roles.isOnlyPublisher(role) || Roles.isSubscriptionPublisher(role);
  }

  /** Any non-publisher staff (role < 60) */
  static isStaff(role) {
    return !!role && role < Roles.PUBLISHER;
  }
}

export default Roles;
